import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ErrorModal from "../../components/ErrorModal";
import {
  ABSENCE_LABELS,
  MONTH_NAMES,
  PERIOD_STATUS,
  TONE_CLASSES,
  TONE_COLORS,
  codeTone,
  apiErrorMessage,
  buildYearGrid,
  formatDate,
  formatEuro,
  formatHoursMinutes,
  trimSeconds,
  weekdayName,
} from "../../utils/timesheets";


function cellText(cell) {
  if (!cell) return "";
  switch (cell.kind) {
    case "hours": return formatHoursMinutes(cell.minutes);
    case "items": return "…";
    case "sick": return "Z";
    case "absent": return "A";
    default: return cell.codes.join(",");
  }
}

function describeItem(item, codes = {}) {
  if (item.entry_type === "absence") return ABSENCE_LABELS[item.absence_type] || "Afwezig";
  const label = item.entry_type === "code" ? `${item.code} · ${codes[item.code] || ""}`.trim() : item.line_description;
  if (item.compensation_type === "per_item") return `${label}: ${Number(item.quantity)}×`;
  const times = item.start_time ? `${trimSeconds(item.start_time)}–${trimSeconds(item.end_time)}` : "";
  const pauze = item.break_minutes ? ` (pauze ${formatHoursMinutes(item.break_minutes)})` : "";
  return `${label} ${times}${pauze}${item.minutes ? ` = ${formatHoursMinutes(item.minutes)}` : ""}`.trim();
}

/** Tab "Jaaroverzicht": raster met een rij per maand en een cel per dag. Hover (of tik) toont de details in een venstertje bij de cursor. */
export default function TimesheetYear({ memberId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [tip, setTip] = useState(null); // { month, day, x, y }: cel onder de cursor en de positie van de cursor
  const [error, setError] = useState("");

  useEffect(() => {
    if (!memberId) return;
    setTip(null);
    setData(null);
    apiFetch(`/timesheets/year?year=${year}&member_id=${memberId}`)
      .then(setData)
      .catch((err) => setError(apiErrorMessage(err)));
  }, [memberId, year]);

  const grid = useMemo(() => (data ? buildYearGrid(data.entries, data.statuses) : null), [data]);
  const detail = tip && grid ? grid.months[tip.month - 1].days[tip.day] : null;
  const detailDate = tip ? `${year}-${String(tip.month).padStart(2, "0")}-${String(tip.day).padStart(2, "0")}` : null;

  // het venstertje volgt de cursor en klapt om naar links/boven vlak bij de rand van het scherm
  const tipStyle = tip
    ? {
        left: tip.x > window.innerWidth - 360 ? tip.x - 340 : tip.x + 16,
        top: tip.y > window.innerHeight - 180 ? tip.y - 150 : tip.y + 16,
      }
    : null;
  const showCost = grid?.hasCost;

  // de legenda toont enkel wat in dit jaar voorkomt: uren, handelingen, Z/A (extern) en de gebruikte codes (intern)
  const legend = useMemo(() => {
    if (!grid) return [];
    const kinds = new Set();
    const codesUsed = [];
    grid.months.forEach((m) =>
      Object.values(m.days).forEach((cell) => {
        cell.kinds.forEach((k) => kinds.add(k));
        if (cell.minutes > 0) kinds.add("hours");
        cell.codes.forEach((c) => { if (!codesUsed.includes(c)) codesUsed.push(c); });
      })
    );
    const items = [];
    if (kinds.has("hours")) items.push({ key: "hours", symbol: "7u30", label: "gewerkte uren", tone: "green" });
    if (kinds.has("items")) items.push({ key: "items", symbol: "…", label: "handelingen", tone: "green" });
    if (kinds.has("sick")) items.push({ key: "sick", symbol: "Z", label: "ziek", tone: "red" });
    if (kinds.has("absent")) items.push({ key: "absent", symbol: "A", label: "afwezig", tone: "orange" });
    codesUsed.sort().forEach((c) =>
      items.push({ key: `code-${c}`, symbol: c, label: data?.codes?.[c] || "code", tone: codeTone(c) })
    );
    return items;
  }, [grid, data]);

  return (
    <div>
      {error && <ErrorModal message={error} onClose={() => setError("")} />}
      <div className="grid grid-cols-3 items-center bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 mb-4">
        <div />
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setYear(year - 1)} aria-label="Vorig jaar" className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">‹</button>
          <div className="min-w-[4rem] text-center font-semibold text-gray-800">{year}</div>
          <button onClick={() => setYear(year + 1)} aria-label="Volgend jaar" className="px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50">›</button>
        </div>
        <div />
      </div>

      <div
        className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto"
        onClick={(e) => { if (!e.target.closest("[data-cell]")) setTip(null); }}
      >
        <table className="text-xs border-collapse" style={{ minWidth: 1100 }}>
          <thead>
            <tr className="bg-gray-50 text-gray-500">
              <th className="px-2 py-2 text-left sticky left-0 bg-gray-50">Maand</th>
              <th className="px-2 py-2 text-right">Uren</th>
              {showCost && <th className="px-2 py-2 text-right">Kost</th>}
              {Array.from({ length: 31 }, (_, i) => <th key={i} className="px-0 py-2 w-9 text-center font-medium">{i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {!grid && <tr><td colSpan={34} className="px-3 py-10 text-center text-gray-400">Laden...</td></tr>}
            {grid && grid.months.map((m) => {
              const daysInMonth = new Date(year, m.month, 0).getDate();
              const badge = PERIOD_STATUS[m.status];
              return (
                <tr key={m.month} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 sticky left-0 bg-white whitespace-nowrap capitalize">
                    {MONTH_NAMES[m.month - 1]}
                    {m.status !== "open" && <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${badge.className}`} title={badge.label}>{badge.label}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium whitespace-nowrap">{m.minutes ? formatHoursMinutes(m.minutes) : ""}</td>
                  {showCost && <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{m.hasCost ? formatEuro(m.cost) : ""}</td>}
                  {Array.from({ length: 31 }, (_, i) => {
                    const day = i + 1;
                    if (day > daysInMonth) return <td key={day} className="bg-gray-100"></td>;
                    const iso = `${year}-${String(m.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const weekend = ["Zaterdag", "Zondag"].includes(weekdayName(iso));
                    const cell = m.days[day];
                    const isActive = tip && tip.month === m.month && tip.day === day;
                    return (
                      <td
                        key={day}
                        data-testid={`cell-${m.month}-${day}`}
                        data-cell={cell ? "" : undefined}
                        data-split={cell && cell.secondary ? cell.secondary.tone : undefined}
                        style={cell && cell.secondary ? {
                          position: "relative",
                          backgroundImage: `linear-gradient(to bottom right, ${TONE_COLORS.green} 50%, ${TONE_COLORS[cell.secondary.tone]} 50%)`,
                        } : undefined}
                        onMouseMove={(e) => cell && setTip({ month: m.month, day, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setTip(null)}
                        onClick={(e) => cell && setTip({ month: m.month, day, x: e.clientX, y: e.clientY })}
                        className={`w-9 h-8 text-center text-[11px] tabular-nums border-l border-gray-50 ${weekend && !cell ? "bg-gray-50" : ""} ${
                          cell ? "cursor-pointer" : ""} ${isActive ? "ring-2 ring-inset ring-blue-500" : ""} ${
                          cell ? TONE_CLASSES[cell.tone] : ""}`}
                      >
                        {cell && cell.secondary ? (
                          <>
                            <span className="absolute left-0.5 top-0 text-[10px] leading-4">{formatHoursMinutes(cell.minutes)}</span>
                            <span className="absolute right-0.5 bottom-0 text-[10px] leading-4">{cell.secondary.text}</span>
                          </>
                        ) : (
                          cellText(cell)
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {grid && (
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800">
                <td className="px-2 py-2 sticky left-0 bg-gray-50">Totaal {year}</td>
                <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{formatHoursMinutes(grid.total.minutes)}</td>
                {showCost && <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{formatEuro(grid.total.cost)}</td>}
                <td colSpan={31} className="px-2 py-2 text-left font-normal text-gray-500">
                  {showCost
                    ? `Waarvan goedgekeurd: ${formatHoursMinutes(grid.approved.minutes)} · ${formatEuro(grid.approved.cost)}`
                    : `Waarvan goedgekeurd: ${formatHoursMinutes(grid.approved.minutes)}`}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {detail && tipStyle && (
        <div
          role="tooltip"
          className="fixed z-50 w-80 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-lg pointer-events-none"
          style={tipStyle}
        >
          <div className="font-medium text-gray-800">{weekdayName(detailDate)} {formatDate(detailDate)}</div>
          {detail.items.map((item) => (
            <div key={item.id}>
              {describeItem(item, data?.codes)}{item.cost !== null && item.cost !== undefined ? ` · ${formatEuro(item.cost)}` : ""}
              {item.note ? <span className="text-gray-400"> — {item.note}</span> : null}
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500">Ga met de muis over een dag (of tik erop) voor start- en einduur{showCost ? " en kost" : ""}. Een diagonaal gedeelde dag heeft uren én een afwezigheid: links boven de uren, rechts onder de afwezigheid.</p>
      {legend.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600" aria-label="Legenda">
          {legend.map((item) => (
            <li key={item.key} className="flex items-center gap-1.5">
              <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] ${TONE_CLASSES[item.tone]}`}>{item.symbol}</span>
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
